import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getJuryCampaigns, getJuryMsgMembers, getJuryMsgMyComments, getJuryThreads,
  createJuryThread, getJuryThread, replyJuryThread,
} from '../../api';

// Rafraichissement des conversations : la messagerie n'a pas de canal temps
// reel, on interroge le serveur a intervalle regulier tant que la page est ouverte.
const POLL_MS = 30000;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
      + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

/** Sélecteur des commentaires à joindre à un message. */
function CommentPicker({ comments, selected, onChange }) {
  const [open, setOpen] = useState(false);
  if (!comments.length) {
    return (
      <div style={{ fontSize: 12, color: 'var(--text-light)' }}>
        Aucun commentaire à joindre : rédigez d'abord votre appréciation sur une fiche startup.
      </div>
    );
  }
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  return (
    <div>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ border: '1px solid var(--border)', background: 'white', borderRadius: 6,
          padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--primary)' }}>
        📎 Joindre mes commentaires{selected.length ? ` (${selected.length})` : ''} {open ? '▴' : '▾'}
      </button>
      {open && (
        <div style={{ marginTop: 8, maxHeight: 190, overflowY: 'auto', border: '1px solid var(--border)',
          borderRadius: 8, padding: 6, background: 'var(--bg-secondary)' }}>
          {comments.map(c => (
            <label key={c.startup_id} style={{ display: 'flex', gap: 8, padding: '6px 8px', borderRadius: 6,
              cursor: 'pointer', alignItems: 'flex-start',
              background: selected.includes(c.startup_id) ? '#EEF2FD' : 'transparent' }}>
              <input type="checkbox" checked={selected.includes(c.startup_id)}
                onChange={() => toggle(c.startup_id)} style={{ marginTop: 3 }} />
              <span style={{ fontSize: 12.5, lineHeight: 1.4 }}>
                <strong>{c.startup_name}</strong>
                <span style={{ color: 'var(--text-light)' }}> — {c.comment.length > 90 ? c.comment.slice(0, 90) + '…' : c.comment}</span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Nom de startup cliquable dans un commentaire joint.
 *
 * Survol : affiche le commentaire que le LECTEUR a lui-même rédigé sur cette
 * startup, s'il en a un — pour comparer son avis à celui reçu sans quitter la
 * conversation. Clic : ouvre la fiche dans l'espace jury.
 *
 * L'info-bulle est en position fixe, calée sur le lien : la liste des messages
 * défile dans un conteneur à hauteur bornée, qui rognerait une bulle positionnée
 * en absolu.
 */
function StartupLink({ a, fromMe, myComment, onOpen }) {
  const [tip, setTip] = useState(null);
  const ref = useRef(null);
  if (!a.startup_id) {
    // Startup supprimée depuis l'envoi : plus de fiche à ouvrir.
    return <span>{a.startup_name}</span>;
  }
  const show = () => {
    const r = ref.current.getBoundingClientRect();
    // Sur téléphone, 300 px dépassent l'écran : la bulle prend la largeur
    // disponible. Elle bascule au-dessus du lien dans la moitié basse de
    // l'écran, sinon elle sortirait par le bas.
    const width = Math.min(300, window.innerWidth - 16);
    const above = r.bottom > window.innerHeight * 0.6;
    setTip({
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
      top: above ? undefined : r.bottom + 6,
      bottom: above ? window.innerHeight - r.top + 6 : undefined,
      width,
    });
  };
  return (
    <>
      <button ref={ref} type="button" onClick={() => onOpen(a.startup_id)}
        onMouseEnter={show} onMouseLeave={() => setTip(null)} onFocus={show} onBlur={() => setTip(null)}
        style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
          fontWeight: 700, color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}>
        {a.startup_name}
      </button>
      {tip && (
        <div role="tooltip" style={{ position: 'fixed', left: tip.left, top: tip.top, bottom: tip.bottom,
          width: tip.width, maxHeight: '45vh', overflow: 'hidden', zIndex: 2000,
          background: '#1B1E38', color: 'white', borderRadius: 8, padding: '10px 12px', fontSize: 12.5,
          lineHeight: 1.5, fontWeight: 400, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', pointerEvents: 'none',
          textAlign: 'left', whiteSpace: 'normal' }}>
          {myComment ? (
            <>
              <div style={{ fontWeight: 700, color: '#9CC2FF', marginBottom: 4 }}>Votre commentaire sur {a.startup_name}</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                {myComment.length > 400 ? myComment.slice(0, 400) + '…' : myComment}
              </div>
            </>
          ) : (
            <div style={{ color: 'rgba(255,255,255,0.8)' }}>
              {fromMe ? 'Votre commentaire joint à ce message.' : "Vous n'avez pas encore commenté cette startup."}
            </div>
          )}
          <div style={{ marginTop: 6, fontSize: 11.5, color: '#9CC2FF' }}>Cliquer pour ouvrir la fiche →</div>
        </div>
      )}
    </>
  );
}

/** Commentaire joint, affiché dans un message. */
function AttachmentCard({ a, fromMe, myComment, onOpen }) {
  return (
    <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, fontSize: 12.5, lineHeight: 1.5,
      background: fromMe ? 'rgba(255,255,255,0.14)' : '#F5F3FF',
      borderLeft: `3px solid ${fromMe ? 'rgba(255,255,255,0.6)' : '#9C27B0'}` }}>
      <div style={{ fontWeight: 700, marginBottom: 2 }}>
        📎 Commentaire sur <StartupLink a={a} fromMe={fromMe} myComment={myComment} onOpen={onOpen} />
      </div>
      <div style={{ whiteSpace: 'pre-wrap' }}>{a.comment}</div>
    </div>
  );
}

export default function JuryMessages() {
  const location = useLocation();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [members, setMembers] = useState([]);
  const [myComments, setMyComments] = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  // Nouvelle conversation
  const [allMembers, setAllMembers] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attach, setAttach] = useState([]);

  // Réponse
  const [reply, setReply] = useState('');
  const [replyAttach, setReplyAttach] = useState([]);
  // Incrémenté après chaque envoi : remonte le sélecteur de commentaires pour
  // qu'il se referme, au lieu de rester déplié sous un message déjà parti.
  const [pickerKey, setPickerKey] = useState(0);
  const bottomRef = useRef(null);

  // --- Chargement initial : campagne active du juré ---
  useEffect(() => {
    getJuryCampaigns()
      .then(cs => {
        const c = cs.find(x => x.is_active) || cs[0];
        if (!c) { setLoadError("Vous n'êtes rattaché à aucune campagne : la messagerie est indisponible."); return; }
        setCampaign(c);
      })
      .catch(err => setLoadError(err.message));
  }, []);

  const loadThreads = useCallback(() => {
    if (!campaign) return Promise.resolve();
    return getJuryThreads(campaign.id).then(setThreads).catch(err => setError(err.message));
  }, [campaign]);

  useEffect(() => {
    if (!campaign) return undefined;
    getJuryMsgMembers(campaign.id).then(setMembers).catch(() => {});
    getJuryMsgMyComments(campaign.id).then(setMyComments).catch(() => {});
    loadThreads();
    const t = setInterval(loadThreads, POLL_MS);
    return () => clearInterval(t);
  }, [campaign, loadThreads]);

  // Retour depuis une fiche : /jury/messages?thread=<id> rouvre la conversation.
  const threadParam = new URLSearchParams(location.search).get('thread');

  const openThread = useCallback((id, { silent } = {}) => {
    return getJuryThread(id)
      .then(t => {
        setThread(t); setActiveId(id); setComposing(false);
        if (!silent) setError('');
        // L'ouverture marque la conversation comme lue : on met à jour la liste
        // et la pastille du menu sans attendre le prochain rafraîchissement.
        setThreads(ts => ts.map(x => (x.id === id ? { ...x, unread: 0 } : x)));
        window.dispatchEvent(new Event('jury-messages-read'));
      })
      .catch(err => setError(err.message));
  }, []);

  // Conversation ouverte : rafraîchie elle aussi, pour voir arriver les réponses.
  useEffect(() => {
    if (!activeId) return undefined;
    const t = setInterval(() => openThread(activeId, { silent: true }), POLL_MS);
    return () => clearInterval(t);
  }, [activeId, openThread]);

  useEffect(() => {
    if (campaign && threadParam) openThread(parseInt(threadParam, 10));
  }, [campaign, threadParam, openThread]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [thread]);

  // Commentaires du lecteur, indexés par startup, pour l'info-bulle de survol.
  const myCommentByStartup = Object.fromEntries(myComments.map(c => [c.startup_id, c.comment]));
  const openStartup = (startupId) =>
    navigate(`/jury?fiche=${startupId}${activeId ? `&thread=${activeId}` : '&from=messages'}`);

  const startCompose = () => {
    setComposing(true); setActiveId(null); setThread(null); setError('');
    setAllMembers(false); setRecipients([]); setSubject(''); setBody(''); setAttach([]);
  };

  const sendNew = async (e) => {
    e.preventDefault();
    if (!allMembers && recipients.length === 0) { setError('Choisissez au moins un destinataire.'); return; }
    setSending(true); setError('');
    try {
      const res = await createJuryThread(campaign.id, {
        subject, body, all_members: allMembers, recipient_ids: recipients, attachment_startup_ids: attach,
      });
      await loadThreads();
      await openThread(res.id);
    } catch (err) { setError(err.message); }
    setSending(false);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setError('');
    try {
      await replyJuryThread(activeId, { body: reply, attachment_startup_ids: replyAttach });
      setReply(''); setReplyAttach([]); setPickerKey(k => k + 1);
      await openThread(activeId);
      loadThreads();
    } catch (err) { setError(err.message); }
    setSending(false);
  };

  const toggleRecipient = (id) =>
    setRecipients(r => (r.includes(id) ? r.filter(x => x !== id) : [...r, id]));

  if (loadError) {
    return <div className="container" style={{ padding: '48px 0' }}><div className="card">{loadError}</div></div>;
  }
  if (!campaign) return <div className="loading">Chargement de la messagerie...</div>;

  const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8,
    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ marginBottom: 4 }}>Messagerie du jury</h1>
          <p style={{ color: 'var(--text-light)', fontSize: 13.5, margin: 0 }}>
            Échanges privés entre membres du jury de « {campaign.name} ». Ni l'organisateur ni les
            candidats n'y ont accès.
          </p>
        </div>

        {error && (
          <div style={{ background: '#FFEBEE', color: '#C62828', padding: '10px 14px', borderRadius: 8,
            marginBottom: 14, fontSize: 13.5 }}>{error}</div>
        )}

        <div className="msg-layout">
          {/* ---------------- Liste des conversations ---------------- */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: 14, borderBottom: '1px solid var(--border)' }}>
              <button onClick={startCompose} className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', fontSize: 14 }}>
                ✉️ Nouveau message
              </button>
            </div>
            {/* Empilée sous la liste sur téléphone, la conversation serait
                repoussée hors de l'écran par une liste de 620 px. */}
            <div style={{ maxHeight: 'min(620px, 45vh)', overflowY: 'auto' }}>
              {threads.length === 0 && (
                <div style={{ padding: 22, fontSize: 13, color: 'var(--text-light)', textAlign: 'center' }}>
                  Aucune conversation pour l'instant.
                </div>
              )}
              {threads.map(t => (
                <button key={t.id} onClick={() => openThread(t.id)}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none',
                    borderBottom: '1px solid var(--border)', padding: '12px 14px', cursor: 'pointer',
                    fontFamily: 'inherit', background: activeId === t.id ? '#EEF2FD' : 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                    <span style={{ fontSize: 14, fontWeight: t.unread ? 700 : 600, color: 'var(--primary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
                    <span style={{ fontSize: 11, color: 'var(--text-light)', flexShrink: 0 }}>
                      {fmtDate(t.last_message?.at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-light)', margin: '2px 0 4px' }}>
                    {t.is_all_members ? 'Tous les membres' : t.participants.join(', ')}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 12.5, color: t.unread ? '#1B1E38' : 'var(--text-light)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.last_message ? `${t.last_message.from_me ? 'Vous' : t.last_message.sender} : ${t.last_message.excerpt}` : ''}
                    </span>
                    {t.unread > 0 && (
                      <span style={{ background: '#E74C3C', color: 'white', borderRadius: 10, fontSize: 11,
                        fontWeight: 700, padding: '1px 7px', flexShrink: 0 }}>{t.unread}</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ---------------- Panneau de droite ---------------- */}
          <div className="card">
            {!composing && !thread && (
              <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--text-light)' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>💬</div>
                Sélectionnez une conversation, ou écrivez un nouveau message.
              </div>
            )}

            {/* ----- Nouvelle conversation ----- */}
            {composing && (
              <form onSubmit={sendNew}>
                <h3 style={{ marginTop: 0, marginBottom: 16 }}>Nouveau message</h3>

                <label className="form-label">Destinataires</label>
                {members.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-light)' }}>
                    Aucun autre membre du jury n'est rattaché à cette campagne.
                  </p>
                ) : (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 14 }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 600, fontSize: 13.5,
                      paddingBottom: 8, marginBottom: 8, borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={allMembers} onChange={e => setAllMembers(e.target.checked)} />
                      Tous les membres du jury ({members.length})
                    </label>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4,
                      opacity: allMembers ? 0.45 : 1 }}>
                      {members.map(m => (
                        <label key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13,
                          cursor: allMembers ? 'default' : 'pointer', padding: '3px 0' }}>
                          <input type="checkbox" disabled={allMembers}
                            checked={allMembers || recipients.includes(m.id)} onChange={() => toggleRecipient(m.id)} />
                          <span>{m.name}{m.company && <span style={{ color: 'var(--text-light)' }}> · {m.company}</span>}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <label className="form-label">Objet</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={200} required
                  style={{ ...inputStyle, marginBottom: 14 }} placeholder="Ex. : Avis sur le dossier EVIAS" />

                <label className="form-label">Message</label>
                <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} maxLength={5000} required
                  style={{ ...inputStyle, resize: 'vertical', marginBottom: 12 }} />

                <CommentPicker comments={myComments} selected={attach} onChange={setAttach} />

                <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
                  <button type="submit" className="btn btn-primary" disabled={sending || members.length === 0}>
                    {sending ? 'Envoi...' : 'Envoyer'}
                  </button>
                  <button type="button" onClick={() => setComposing(false)} className="btn btn-outline">Annuler</button>
                </div>
              </form>
            )}

            {/* ----- Conversation ouverte ----- */}
            {thread && !composing && (
              <div>
                <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12, marginBottom: 12 }}>
                  <h3 style={{ margin: 0 }}>{thread.subject}</h3>
                  <div style={{ fontSize: 12.5, color: 'var(--text-light)', marginTop: 4 }}>
                    {thread.is_all_members ? 'Tous les membres : ' : 'Participants : '}
                    {thread.participants.map(p => (p.is_me ? 'vous' : p.name)).join(', ')}
                  </div>
                </div>

                <div style={{ maxHeight: 'min(470px, 55vh)', overflowY: 'auto', padding: '4px 2px' }}>
                  {thread.messages.map(m => (
                    <div key={m.id} style={{ display: 'flex', justifyContent: m.from_me ? 'flex-end' : 'flex-start',
                      marginBottom: 12 }}>
                      <div style={{ maxWidth: '88%' }}>
                        <div style={{ fontSize: 11, color: 'var(--text-light)', marginBottom: 3,
                          textAlign: m.from_me ? 'right' : 'left' }}>
                          {m.from_me ? 'Vous' : m.sender} · {fmtDate(m.created_at)}
                        </div>
                        <div style={{ padding: '10px 13px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.5,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                          background: m.from_me ? '#4A71E5' : 'var(--bg-secondary)',
                          color: m.from_me ? 'white' : '#1B1E38' }}>
                          {m.body}
                          {m.attachments.map((a, i) => (
                            <AttachmentCard key={i} a={a} fromMe={m.from_me}
                              myComment={myCommentByStartup[a.startup_id]} onOpen={openStartup} />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>

                <form onSubmit={sendReply} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 6 }}>
                  <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={5000}
                    placeholder="Votre réponse..." style={{ ...inputStyle, resize: 'vertical', marginBottom: 10 }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start',
                    flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <CommentPicker key={pickerKey} comments={myComments} selected={replyAttach} onChange={setReplyAttach} />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={sending || !reply.trim()}>
                      {sending ? 'Envoi...' : 'Répondre'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
